import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { ExportOutlined } from '@ant-design/icons';
import { Divider, Flex, Skeleton, Space, Tag, Typography, theme } from 'antd';
import { StatusDotTag } from '@/components/common/StatusDotTag';
import { useVisibleOnce } from '@/hooks/useVisibleOnce';
import { statusTone } from '@/components/common/statusTone';
import { collectionIssues, holdsPublications } from './collectionFacts';
import { useCollectionPublicationCount } from './useCollectionPublicationCount';
import type { LineupCollectionLookups } from './useLineupCollections';

interface CollectionRowContentProps {
  collectionId: number;
  lookups: LineupCollectionLookups;
  /** Advisory deleted / mod-status tags. */
  showIssues?: boolean;
  /** One count query per row — off for consumers with long lists. */
  showCount?: boolean;
  /** Consumer-specific diagnostics, shown after a divider. */
  extra?: ReactNode;
}

const PublicationCountTag = ({ collectionId }: { collectionId: number }) => {
  const t = useTranslate();
  // Long lineups render dozens of rows; each count is fetched only once its
  // row scrolls into view, not all at once when the tab mounts.
  const { ref, visible } = useVisibleOnce<HTMLSpanElement>();
  const { count, isLoading } = useCollectionPublicationCount(
    collectionId,
    visible
  );

  // The span stays when there is nothing to show — it is what the
  // visibility observer watches.
  return (
    <span ref={ref}>
      {!isLoading && count != null && (
        <Tag style={{ marginInlineEnd: 0 }}>
          {t('user_catalog.common.collections_editor.publications', { count })}
        </Tag>
      )}
    </span>
  );
};

/** Display face of a collection row: name, kind, how many publications it
 * currently resolves to, moderation state. */
export const CollectionRowContent = ({
  collectionId,
  lookups,
  showIssues = true,
  showCount = true,
  extra,
}: CollectionRowContentProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const collection = lookups.collectionById.get(collectionId);

  if (!collection && lookups.isLoading) {
    return (
      <Skeleton
        active
        title={false}
        paragraph={{ rows: 2, width: ['50%', '30%'] }}
      />
    );
  }

  const issues =
    showIssues && !lookups.isLoading ? collectionIssues(collection) : [];

  return (
    <Flex vertical style={{ minWidth: 0 }}>
      <Space size={token.marginXXS}>
        <Typography.Text strong ellipsis>
          {collection?.name ?? `#${collectionId}`}
        </Typography.Text>
        {/* The link must not count as a double-click on the row. */}
        <Typography.Link
          href={`/user_catalog/collections/${collectionId}`}
          target="_blank"
          rel="noopener noreferrer"
          onDoubleClick={(event) => event.stopPropagation()}
          aria-label={t('user_catalog.common.collections_editor.open')}
        >
          <ExportOutlined />
        </Typography.Link>
      </Space>

      <Space size={token.marginXXS} wrap style={{ marginTop: token.marginXXS }}>
        {issues.includes('deleted') && (
          <Tag color="red" style={{ marginInlineEnd: 0 }}>
            {t('user_catalog.common.collections_editor.deleted')}
          </Tag>
        )}

        {collection && (
          <StatusDotTag
            // The kind is the tag's subject — no second label for it.
            label={t(
              `user_catalog.collections.kind.${collection.collection_kind}`
            )}
            status={t(`user_catalog.common.status.${collection.mod_status}`)}
            tone={statusTone(collection.mod_status)}
          />
        )}

        {showCount && holdsPublications(collection) && (
          <PublicationCountTag collectionId={collectionId} />
        )}

        {extra && (
          <>
            <Divider
              type="vertical"
              style={{
                margin: 0,
                height: token.fontSizeHeading5,
                borderColor: token.colorTextTertiary,
              }}
            />
            {extra}
          </>
        )}
      </Space>
    </Flex>
  );
};
